import React, { useState, useEffect, useContext, useRef } from "react";
import { useTheme } from "@mui/material/styles";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import whatsappIcon from "../../assets/nopicture.png";
import { i18n } from "../../translate/i18n";
import { PersonOutlined, CloseOutlined, CheckOutlined } from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Avatar, Grid, Input, Paper, Tab, Tabs, Box, Typography, IconButton } from "@mui/material";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import useGoogleIdentity from "../../hooks/useGoogleIdentity";

const backendUrl = getBackendUrl();

// Mismo ícono de Google que usa Login/index.js -- ver TAREA: vinculación
// de Google, "mismo ícono y tratamiento del login".
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
  </svg>
);

// Ver TAREA: vinculación de Google desde el perfil -- reemplaza el
// campo de texto emailAlterno de la Fase 2. Sin vincular: botón que
// dispara el mismo flujo de Google Identity Services que el login
// (useGoogleIdentity). Vinculado: bloque de acento con el correo y un
// enlace para desvincular, que pide la contraseña actual antes de
// limpiar emailAlterno/googleId (mismo criterio que esAutoEdicion usa
// para el cambio de contraseña).
function GoogleLinkSection({ emailAlterno, onLinked, onUnlinked }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [linking, setLinking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [unlinkError, setUnlinkError] = useState("");
  const [unlinking, setUnlinking] = useState(false);

  const handleCredential = async (idToken) => {
    setLinking(true);
    try {
      const { data } = await api.post("/auth/vincular-google", { idToken });
      toast.success(i18n.t("userModal.google.linkedSuccess"));
      onLinked(data.emailAlterno);
    } catch (err) {
      toastError(err);
    } finally {
      setLinking(false);
    }
  };

  const triggerGoogleLink = useGoogleIdentity(handleCredential);

  const openConfirm = () => {
    setCurrentPassword("");
    setUnlinkError("");
    setConfirmOpen(true);
  };

  const handleUnlink = async () => {
    setUnlinkError("");
    if (!currentPassword) {
      setUnlinkError(i18n.t("userModal.google.currentPasswordRequired"));
      return;
    }
    setUnlinking(true);
    try {
      await api.post("/auth/desvincular-google", { currentPassword });
      toast.success(i18n.t("userModal.google.unlinkedSuccess"));
      setConfirmOpen(false);
      onUnlinked();
    } catch (err) {
      setUnlinkError(err.response?.data?.error || i18n.t("userModal.google.unlinkError"));
    } finally {
      setUnlinking(false);
    }
  };

  if (emailAlterno) {
    return (
      <Box sx={{
        border: `0.5px solid ${isDark ? "rgba(52,168,83,0.35)" : "rgba(52,168,83,0.3)"}`,
        background: isDark ? "rgba(52,168,83,0.08)" : "rgba(52,168,83,0.06)",
        borderRadius: "10px", padding: "12px 14px", marginTop: "6px", marginBottom: "6px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckOutlined sx={{ fontSize: 16, color: "#34A853" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.88)" : "rgba(0,0,0,0.82)" }}>
            {i18n.t("userModal.google.linkedTitle")}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)", marginTop: "4px", marginLeft: "24px" }}>
          {emailAlterno}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)", marginTop: "2px", marginLeft: "24px" }}>
          {i18n.t("userModal.google.linkedHint")}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={openConfirm}
          sx={{
            border: "none", background: "none", cursor: "pointer", padding: 0, marginTop: "8px", marginLeft: "24px",
            fontSize: 12, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
            textDecoration: "underline", "&:hover": { color: "#ef4444" },
          }}
        >
          {i18n.t("userModal.google.unlink")}
        </Box>

        <Dialog open={confirmOpen} onClose={unlinking ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: "14px" } }}>
          <Box sx={{ padding: "20px 22px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, marginBottom: "6px" }}>
              {i18n.t("userModal.google.unlinkConfirmTitle")}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, marginBottom: "14px" }}>
              {i18n.t("userModal.google.unlinkConfirmBody")}
            </Typography>
            <TextField
              type="password"
              fullWidth
              size="small"
              autoFocus
              label={i18n.t("userModal.google.currentPassword")}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            {unlinkError && (
              <Typography sx={{ fontSize: 12, color: "#ef4444", marginTop: "8px" }}>{unlinkError}</Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginTop: "18px" }}>
              <Button onClick={() => setConfirmOpen(false)} disabled={unlinking} sx={{ textTransform: "none" }}>
                {i18n.t("userModal.buttons.cancel")}
              </Button>
              <Button onClick={handleUnlink} disabled={unlinking} variant="contained" color="error" sx={{ textTransform: "none" }}
                startIcon={unlinking ? <CircularProgress size={14} color="inherit" /> : null}>
                {i18n.t("userModal.google.unlink")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{
      border: `0.5px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
      borderRadius: "10px", padding: "12px 14px", marginTop: "6px", marginBottom: "6px",
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.78)", marginBottom: "4px" }}>
        {i18n.t("userModal.google.title")}
      </Typography>
      <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, marginBottom: "10px" }}>
        {i18n.t("userModal.google.description")}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={triggerGoogleLink}
        disabled={linking}
        sx={{
          height: 40, borderRadius: "9px",
          border: isDark ? "0.5px solid rgba(255,255,255,0.15)" : "0.5px solid rgba(0,0,0,0.15)",
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
          fontSize: 13, fontWeight: 600, cursor: linking ? "default" : "pointer", width: "100%",
          color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.70)",
          opacity: linking ? 0.6 : 1,
          "&:hover": !linking && {
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.02)",
          },
        }}
      >
        {linking ? <CircularProgress size={16} /> : <GoogleIcon />}
        {i18n.t("userModal.google.linkButton")}
      </Box>
    </Box>
  );
}

const formatDateForInput = (date) => {
  if (!date) return '';
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  return dateString;
};


const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId }) => {
  const theme = useTheme();

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled"
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  // const [allTicket, setAllTicket] = useState("disable");
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate)
        }));

        const { profileImage } = data;
        setProfileUrl(
          `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
        );

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return data.user.profileImage;
      } catch (error) {
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled"
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Recarregar página se for o usuário logado para atualizar a interface
      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius:'14px', border:`0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}` } }}
      >
        <DialogTitle sx={{ padding:'18px 22px 14px', borderBottom:`0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}` }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Box sx={{ width:30, height:30, borderRadius:'8px', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <PersonOutlined sx={{ fontSize:16, color:'#0399FF' }} />
              </Box>
              <Typography sx={{ fontSize:15, fontWeight:600, color:(theme.palette.mode==="dark"?"rgba(255,255,255,0.82)":"rgba(0,0,0,0.82)") }}>
                {userId ? i18n.t("userModal.title.edit") : i18n.t("userModal.title.add")}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small"
              sx={{ borderRadius:'6px', color:(theme.palette.mode==="dark"?"rgba(255,255,255,0.3)":"rgba(0,0,0,0.3)"), '&:hover':{ background:(theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") } }}>
              <CloseOutlined sx={{ fontSize:16 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <Paper elevation={1}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  <Tab
                    label={i18n.t("userModal.tabs.permissions")}
                    value={"permissions"}
                  />
                </Tabs>
              </Paper>
              <Paper elevation={0}>
                <DialogContent sx={{ padding:'18px 22px', '&::-webkit-scrollbar':{ width:'3px' }, '&::-webkit-scrollbar-thumb':{ background:(theme.palette.mode==="dark"?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"), borderRadius:'3px' } }}>
                  <TabPanel
                    sx={{ display: "flex", flexWrap: "wrap" }}
                    value={tab}
                    name={"general"}
                  >
                    <Grid
                      container
                      spacing={1}
                      alignContent="center"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FormControl sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <AvatarUploader
                          setAvatar={setAvatar}
                          avatar={user.profileImage}
                          companyId={user.companyId}
                        />
                        {user.profileImage && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              user.profileImage = null;
                              setFieldValue("profileImage", null);
                              setAvatar(null);
                            }}
                          >
                            {i18n.t("userModal.title.removeImage")}
                          </Button>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.password")}
                          type="password"
                          name="password"
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 8, xl: 8 }}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.email")}
                          name="email"
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4, xl: 4 }}>
                        <FormControl
                          variant="outlined"
                          //className={classes.formControl}
                          margin="dense"
                          fullWidth
                        >
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <>
                                <InputLabel id="profile-selection-input-label">
                                  {i18n.t("userModal.form.profile")}
                                </InputLabel>

                                <Field
                                  as={Select}
                                  label={i18n.t("userModal.form.profile")}
                                  name="profile"
                                  labelId="profile-selection-label"
                                  id="profile-selection"
                                  required
                                >
                                  <MenuItem value="admin">Admin</MenuItem>
                                  <MenuItem value="user">User</MenuItem>
                                </Field>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    {/* Ver TAREA: vinculación de Google -- reemplaza el campo de
                        texto emailAlterno (cualquiera podía escribir el Gmail
                        de otra persona ahí). Solo aparece cuando el usuario
                        edita SU PROPIA cuenta -- un admin editando a otro no
                        puede vincular la cuenta de Google ajena. */}
                    {userId === loggedInUser.id && (
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                          <GoogleLinkSection
                            emailAlterno={user.emailAlterno}
                            onLinked={(email) => setUser((prev) => ({ ...prev, emailAlterno: email }))}
                            onUnlinked={() => setUser((prev) => ({ ...prev, emailAlterno: null }))}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editQueues"
                          yes={() => (
                            <QueueSelect
                              selectedQueueIds={selectedQueueIds}
                              onChange={(values) => setSelectedQueueIds(values)}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editProfile"
                          yes={() => (
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.whatsapp")}
                              </InputLabel>
                              <Field
                                as={Select}
                                value={whatsappId}
                                onChange={(e) => setWhatsappId(e.target.value)}
                                label={i18n.t("userModal.form.whatsapp")}
                              >
                                <MenuItem value={""}>&nbsp;</MenuItem>
                                {whatsApps.map((whatsapp) => (
                                  <MenuItem
                                    key={whatsapp.id}
                                    value={whatsapp.id}
                                  >
                                    {whatsapp.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.startWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={startWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="startWork"
                              error={
                                touched.startWork && Boolean(errors.startWork)
                              }
                              helperText={touched.startWork && errors.startWork}
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.endWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={endWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="endWork"
                              error={touched.endWork && Boolean(errors.endWork)}
                              helperText={touched.endWork && errors.endWork}
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                        </Grid>
                      )}
                    />

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <Field
                          as={TextField}
                          label="Fecha de nacimiento"
                          type="date"
                          name="birthDate"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText="Para notificaciones de cumpleaños"
                          onChange={(e) => {
                            const formattedDate = parseDateFromInput(e.target.value);
                            setFieldValue('birthDate', formattedDate);
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.farewellMessage")}
                      type="farewellMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="farewellMessage"
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        touched.farewellMessage && errors.farewellMessage
                      }
                      variant="outlined"
                      margin="dense"
                    />

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        >
                          <>
                            <InputLabel>
                              {i18n.t("userModal.form.defaultTheme")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultTheme")}
                              name="defaultTheme"
                              type="defaultTheme"
                              required
                            >
                              <MenuItem value="light">
                                {i18n.t("userModal.form.defaultThemeLight")}
                              </MenuItem>
                              <MenuItem value="dark">
                                {i18n.t("userModal.form.defaultThemeDark")}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        >
                          <>
                            <InputLabel>
                              {i18n.t("userModal.form.defaultMenu")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultMenu")}
                              name="defaultMenu"
                              type="defaultMenu"
                              required
                            >
                              <MenuItem value={"open"}>
                                {i18n.t("userModal.form.defaultMenuOpen")}
                              </MenuItem>
                              <MenuItem value={"closed"}>
                                {i18n.t("userModal.form.defaultMenuClosed")}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TabPanel>
                  <TabPanel
                    sx={{ display: "flex", flexWrap: "wrap" }}
                    value={tab}
                    name={"permissions"}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allTicket")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.allTicket")}
                                    name="allTicket"
                                    type="allTicket"
                                    required
                                  >
                                    <MenuItem value="enable">
                                      {i18n.t("userModal.form.allTicketEnable")}
                                    </MenuItem>
                                    <MenuItem value="disable">
                                      {i18n.t(
                                        "userModal.form.allTicketDisable"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allowGroup")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.allowGroup")}
                                    name="allowGroup"
                                    type="allowGroup"
                                    required
                                  >
                                    <MenuItem value={true}>
                                      {i18n.t("userModal.form.allTicketEnable")}
                                    </MenuItem>
                                    <MenuItem value={false}>
                                      {i18n.t(
                                        "userModal.form.allTicketDisable"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allHistoric")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.allHistoric")}
                                    name="allHistoric"
                                    type="allHistoric"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allUserChat")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.allUserChat")}
                                    name="allUserChat"
                                    type="allUserChat"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t(
                                      "userModal.form.userClosePendingTicket"
                                    )}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.userClosePendingTicket"
                                    )}
                                    name="userClosePendingTicket"
                                    type="userClosePendingTicket"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allowSeeMessagesInPendingTickets")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.allowSeeMessagesInPendingTickets")}
                                    name="allowSeeMessagesInPendingTickets"
                                    type="allowSeeMessagesInPendingTickets"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allowConnections")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.allowConnections"
                                    )}
                                    name="allowConnections"
                                    type="allowConnections"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.showDashboard")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.showDashboard"
                                    )}
                                    name="showDashboard"
                                    type="showDashboard"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.allowRealTime")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.allowRealTime"
                                    )}
                                    name="allowRealTime"
                                    type="allowRealTime"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.showContacts")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.showContacts"
                                    )}
                                    name="showContacts"
                                    type="showContacts"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.showCampaign")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t(
                                      "userModal.form.showCampaign"
                                    )}
                                    name="showCampaign"
                                    type="showCampaign"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    {i18n.t("userModal.form.showFlow")}
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label={i18n.t("userModal.form.showFlow")}
                                    name="showFlow"
                                    type="showFlow"
                                    required
                                  >
                                    <MenuItem value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6, xl: 6 }}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <>
                                  <InputLabel>
                                    Finalização com Valor de Venda
                                  </InputLabel>

                                  <Field
                                    as={Select}
                                    label="Finalização com Valor de Venda"
                                    name="finalizacaoComValorVendaAtiva"
                                    type="finalizacaoComValorVendaAtiva"
                                    required
                                  >
                                    <MenuItem value="false">
                                      Desactivado
                                    </MenuItem>
                                    <MenuItem value="true">Habilitado</MenuItem>
                                  </Field>
                                </>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    />
                  </TabPanel>
                </DialogContent>
              </Paper>
              <DialogActions sx={{ padding:'12px 22px', borderTop:`0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, background: theme.palette.mode==="dark"?"#0d1424":"#fafafa", gap:1, justifyContent:'flex-end' }}>
                <Button onClick={handleClose}
                  sx={{ fontSize:12, fontWeight:500, color:(theme.palette.mode==="dark"?"rgba(255,255,255,0.55)":"rgba(0,0,0,0.55)"), border:`0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"}`, background: theme.palette.background.paper, borderRadius:'8px', textTransform:'none', '&:hover':{ background: theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"#f4f4f4" } }}>
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}
                  sx={{ fontSize:12, fontWeight:600, color:'#fff', background:'#0399FF', borderRadius:'8px', textTransform:'none', px:2, '&:hover':{ background:'#0284d4' }, '&.Mui-disabled':{ background:'#b0d9ff', color:'#fff' } }}
                  startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <CheckOutlined sx={{ fontSize:14 }} />}>
                  {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default UserModal;